
// components/FeaturedEventsComponent.jsx
"use client";

import { useRef, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import FeaturedEventCard from "./FeaturedEventCard.jsx";
import ScrollButtons from "./ScrollButtons.jsx";
import { ScrollArea } from "../../../ui/scrollArea.jsx";
import { AnimatePresence } from "framer-motion";
import useEventStore from "../../../../Store/EventStore.jsx";

/**
 * FeaturedEventsComponent - Displays a horizontal scrollable list of featured events
 * 
 * Shows featured events in a scrollable carousel with left/right navigation buttons.
 * Allows users to add featured events to their collection.
 * 
 * @component
 * @param {Object} props
 * @param {Array} props.events - Array of featured event objects
 * 
 * @example
 * <FeaturedEventsComponent events={featuredEvents} />
 */

export default function FeaturedEventsComponent({ events }) {
  const [addedEvents, setAddedEvents] = useState([]);
  const scrollRef = useRef(null);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);
  const { handleAddEvent: addEvents } = useEventStore();

  // Update the scroll visibility status when scrolling
  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      const isScrollable = scrollWidth > clientWidth;

      setShowLeftScroll(isScrollable && scrollLeft > 0);
      setShowRightScroll(isScrollable && scrollLeft < scrollWidth - clientWidth);
    }
  };

  useEffect(() => {
    handleScroll(); // Initial check

    window.addEventListener('resize', handleScroll);
    return () => window.removeEventListener('resize', handleScroll);
  }, [events]); // Replace 'events' with your data array or dependencies


  // Function to scroll left or right based on the button click
  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount =
        direction === "left"
          ? -scrollRef.current.clientWidth / 2
          : scrollRef.current.clientWidth / 2; // Scroll by half the width of the visible area
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  // Function to add an event using the EventStore
  const handleAddEvent = (event) => {
    addEvents(event);
  };

  // Attach the scroll listener to update button visibility
  useEffect(() => {
    const currentRef = scrollRef.current;
    if (currentRef) {
      currentRef.addEventListener("scroll", handleScroll);
      return () => currentRef.removeEventListener("scroll", handleScroll);
    }
  }, []);

  return (
    <div className="min-w-sm max-w-6xl mx-auto space-y-12">
      <div className="relative">
        <ScrollArea ref={scrollRef} className="w-full rounded-full bg-grey-500 backdrop-blur-sm">
          <div className="flex w-max space-x-4 p-6">
            {events.map((event) => (
              <FeaturedEventCard
                key={event._id}
                event={event}
                onAdd={handleAddEvent}
                isAdded={addedEvents.some((e) => e.id === event.id)}
              />
            ))}
          </div>
        </ScrollArea>
        <ScrollButtons
          showLeftScroll={showLeftScroll}
          showRightScroll={showRightScroll}
          onScroll={scroll}
        />
      </div>
    </div>
  );
}